import {useMemo} from 'react';
import {useTranslation} from 'react-i18next';
import moment from 'moment';
import AppTable, {ColumnDef} from '../../../../components/common/AppTable';
import {SVG} from '../../../../constants/assetLists';
import {useFormsContext} from '../../../../context/forms/FormsContextProvider';
import {IForm} from '../../forms';
import {formatNumber, getRoleTypeName} from '../../../../utils';
import {ICONS} from '../../../users/data';
import { FormsData, FormStatuses } from '../../../../services/operations/forms';

// Status priority for sorting: published first, then draft, then archived
const STATUS_SORT_ORDER: Record<string, number> = {
  published: 0,
  draft: 1,
  archived: 2,
};

function FormsTable() {
  const {t, i18n} = useTranslation();
  const isHebrew = i18n.language === 'heb';

  const {
    allForms,
    selectedForms,
    setSelectedForms,
    page,
    setPage,
    searchText,
    menuSelectedItem,
  } = useFormsContext();

  // Sort forms by status when archived mode is enabled
  // Order: published → draft → archived
  const sortedForms = useMemo(() => {
    const forms = allForms?.forms || [];

    return [...forms].sort((a, b) => {
      const statusA = a.status ?? 'published';
      const statusB = b.status ?? 'published';
      return (
        (STATUS_SORT_ORDER[statusA] ?? 0) - (STATUS_SORT_ORDER[statusB] ?? 0)
      );
    });
  }, [allForms?.forms, menuSelectedItem]);

  const columns = useMemo<ColumnDef<FormsData>[]>(
    () => [
      {
        title: t('forms.table.formName'),
        field: 'title',
        accessor: 'title',
        sortable: true,
        searchable: true,
        cellClassName: 'w-[150px]',
      },
      {
        title: t('forms.table.creator'),
        field: 'createdBy',
        accessor: r =>
          `${r.createdBy?.firstName ?? ''} ${r.createdBy?.lastName ?? ''}`.trim(),
        sortable: true,
        cellClassName: 'w-[100px]',
      },
      {
        title: t('forms.table.phone'),
        field: 'phone',
        accessor: r => r.createdBy?.phone || '',
        sortable: true,
        cellClassName: 'w-[170px]',
        render: r => (
          <span dir="ltr">
            {r.createdBy?.phone ? formatNumber(r.createdBy.phone) : '-'}
          </span>
        ),
      },
      {
        title: t('forms.table.responses'),
        field: 'responsesCount',
        accessor: r => r.responsesCount || 0,
        cellClassName: 'w-[100px]',
        sortable: true,
      },
      {
        title: t('forms.table.creatorRole'),
        field: 'creatorRole',
        accessor: r => getRoleTypeName(r.createdBy?.roles?.[0]?.roleType) || '',
        sortable: true,
        cellClassName: 'w-[130px]',
        render: r => {
          const role = r.createdBy?.roles?.[0];
          const icon = ICONS?.find(icon => icon.id === role?.icon)?.icon || '';
          const roleName = getRoleTypeName(role?.roleType, isHebrew ? 'he' : 'en');
          return (
            <div className="flex items-center gap-2">
              {icon && (
                <img
                  src={icon}
                  alt="icon"
                  className="w-[20px] h-[20px] object-contain"
                />
              )}
              <span>{roleName || '-'}</span>
            </div>
          );
        },
      },
      {
        title: t('forms.table.status'),
        field: 'status',
        accessor: r => r.status ?? FormStatuses.PUBLISHED,
        sortable: true,
        cellClassName: 'w-[110px]',
        render: r => {
          const status = r.status;
          const statusLabels: Record<string, string> = {
            published: t('forms.table.statusPublished'),
            draft: t('forms.table.statusDraft'),
            archived: t('forms.table.statusArchived'),
          };
          return (
            <div className="flex items-center gap-2">
              {status === FormStatuses.PUBLISHED ? (
                <img src={SVG.tickGreenIcon} className="w-[16px] h-[12px]" />
              ) : status === FormStatuses.ARCHIVED ? (
                <img src={SVG.closeGrayIcon} className="w-[10px] h-[10px]" />
              ) : (
                <div className="w-[8px] h-[8px] rounded-full bg-gray70" />
              )}
              <span>
                {statusLabels[status ?? FormStatuses.PUBLISHED] ?? statusLabels.published}
              </span>
            </div>
          );
        },
      },
      {
        title: t('forms.table.organization'),
        field: 'clientIds',
        accessor: r => {
          const clientNames = r.clients?.map(c => c.name) || [];
          const branchNames = r.branches?.map(b => b.name) || [];
          const allNames = [...clientNames, ...branchNames].filter(Boolean);
          return allNames.join(', ') || '-';
        },
        sortable: true,
        cellClassName: 'w-[180px]',
      },
      {
        title: t('forms.table.createdAt'),
        field: 'createdAt',
        accessor: r => moment(r.createdAt).format('DD/MM/YYYY HH:mm'),
        sortable: true,
        cellClassName: 'w-[150px]',
      },
      {
        title: t('forms.table.updatedAt'),
        field: 'updatedAt',
        accessor: r => moment(r.updatedAt).format('DD/MM/YYYY HH:mm'),
        sortable: true,
        cellClassName: 'w-[150px]',
      },
      {
        title: t('forms.table.assignedTo'),
        field: 'assignedTo',
        accessor: r => {
          const recipientNames = r.recipients?.users?.map(rec => `${rec.firstName} ${rec.lastName}`) || [];
          const departmentNames = r.recipients?.departments?.map(d => d.name) || [];
          return [...recipientNames, ...departmentNames].join(', ') || '-';
        },
        sortable: true,
        cellClassName: 'w-[200px]',
      },
    ],
    [t, isHebrew],
  );

  return (
    <AppTable<FormsData>
      data={sortedForms}
      columns={columns}
      rowKey={r => r._id}
      selectable
      selectedKeys={selectedForms}
      onSelectionChange={setSelectedForms}
      rowDisabled={r => r.status === FormStatuses.ARCHIVED || r.status === FormStatuses.DRAFT}
      showPagination={(allForms?.total || 0) > 50}
      totalRecords={allForms?.total || 0}
      currentPage={page - 1}
      onPageChange={(page: number) => setPage(page + 1)}
      limit={50}
      searchText={searchText}
    />
  );
}

export default FormsTable;
